import {
  CHANGELOG_TAGS,
  DOCS_ORIGIN,
  type ChangelogTag,
} from "./changelogTags";

export type ChangelogEntry = {
  title: string;
  /** Reformatted to an abbreviated month, e.g. "Jul 2, 2026". */
  date: string;
  /**
   * The entry's publish date as an ISO 8601 timestamptz (UTC midnight), e.g.
   * "2026-07-02T00:00:00.000Z". Used to compare against the user's last-seen
   * timestamp to compute the unseen "What's new" count.
   */
  publishedAt: string;
  /** Category tags, text/label only — no emoji. */
  tags: ChangelogTag[];
  /** First sentence of the entry body, capped for display. */
  summary: string;
  /** Fragment id for deep-linking to the entry on the docs page. */
  anchor: string;
  /** Absolute URL of the first image in the body, if any. */
  imageUrl?: string;
};

const DATE_RE = /^([A-Z][a-z]+) (\d{1,2}), (\d{4})$/m;
// The anchor is a link to a `#fragment`; the link text may be empty (`[](#x)`)
// or a literal `#` (`[#](#x)`), so match any link text.
const ANCHOR_RE = /\[[^\]]*\]\(#([^)]+)\)/;
const IMAGE_RE = /!\[[^\]]*\]\(([^)]+)\)/;

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const MONTH_ABBR: Record<string, string> = Object.fromEntries(
  MONTHS.map((m) => [m, m.slice(0, 3)]),
);

const formatDate = (month: string, day: string, year: string): string => {
  const abbr = MONTH_ABBR[month] ?? month;
  return `${abbr} ${day}, ${year}`;
};

/**
 * Convert a "Month Day, Year" date to an ISO 8601 timestamptz at UTC midnight.
 * Unknown month names fall back to January so parsing never throws.
 */
const toTimestamptz = (month: string, day: string, year: string): string => {
  const monthIndex = Math.max(
    0,
    MONTHS.indexOf(month as (typeof MONTHS)[number]),
  );
  return new Date(
    Date.UTC(Number(year), monthIndex, Number(day)),
  ).toISOString();
};

/**
 * Extract the ordered category tags from a meta block. The tag line contains one
 * or more known emoji (with labels); we detect each known emoji and keep the
 * order in which it appears, returning label-only tags.
 */
const extractTags = (metaBlock: string): ChangelogTag[] =>
  Object.entries(CHANGELOG_TAGS)
    .map(([emoji, tag]) => ({ tag, index: metaBlock.indexOf(emoji) }))
    .filter(({ index }) => index >= 0)
    .sort((a, b) => a.index - b.index)
    .map(({ tag }) => tag);

/**
 * Strip inline markdown to plain text — the summary is rendered as text, so link
 * syntax, backticks, and emphasis markers would otherwise show up literally.
 * A plain-text pass, not a full parser; exotic nesting isn't handled (none
 * appears in the source).
 */
const stripInlineMarkdown = (s: string): string =>
  s
    .replace(/!?\[([^\]]+)\]\([^)]+\)/g, "$1") // [text](url) / ![alt](url) -> text
    .replace(/\*\*([^*]+)\*\*/g, "$1") // **bold** -> bold
    .replace(/\*([^*]+)\*/g, "$1") // *italic* -> italic
    .replace(/`([^`]+)`/g, "$1"); // `code` -> code

/**
 * Reduce a first paragraph to a single clean summary line: the first sentence.
 * A complete sentence keeps its terminal period. A colon ends the sentence too —
 * both trailing list lead-ins ("…the following:") and mid-sentence lead-ins
 * ("…a single upgrade: the …") — and is rendered as a period so the summary
 * never trails off into list detail. Visual truncation to fit the panel is left
 * to CSS `line-clamp` on the rendered text, which adapts to the actual width.
 */
const summarize = (paragraph: string): string => {
  // Strip before splitting so markup chars don't skew the sentence boundary
  // (URLs contain "." and ":").
  const text = stripInlineMarkdown(paragraph).trim();
  if (!text) return "";

  // First sentence: up to the first . ! ? : followed by whitespace or end. The
  // whitespace look-ahead keeps "2.28.2" and "3:1" from splitting. A terminating
  // colon becomes a period.
  const match = text.match(/^.*?[.!?:](?=\s|$)/);
  const summary = (match ? match[0] : text).replace(/:$/, ".");

  return /[.!?]$/.test(summary) ? summary : `${summary}.`;
};

/** First non-empty body line that isn't a heading/image/list/code/quote fence. */
const extractSummary = (bodyBlock: string): string => {
  const lines = bodyBlock.split("\n").slice(1); // drop the repeated title line
  const paragraph: string[] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (paragraph.length === 0) {
      if (line === "") continue;
      if (/^(#|!\[|```|[-*>|]|\d+\.)/.test(line)) continue;
      if (ANCHOR_RE.test(line)) continue; // the `[](#anchor)` line, now in the body
      paragraph.push(line);
    } else {
      if (line === "") break; // end of first paragraph
      paragraph.push(line);
    }
  }
  return summarize(paragraph.join(" "));
};

const extractImage = (bodyBlock: string): string | undefined => {
  const match = bodyBlock.match(IMAGE_RE);
  if (!match) return undefined;
  const url = match[1];
  return url.startsWith("http") ? url : `${DOCS_ORIGIN}${url}`;
};

/**
 * Parse the Tiger docs changelog markdown into structured entries.
 *
 * Each entry is authored as two `## <Title>` blocks: a "meta" block (date, tag
 * line) followed by a "body" block whose first line is the `[](#anchor)` deep
 * link and whose first paragraph is the summary. Splitting on `\n## ` yields
 * those blocks in order (`###` subsections stay attached to their body). We pair
 * each meta block with the body block that follows it and return the first
 * `limit` entries.
 */
export const parseChangelog = (
  markdown: string,
  limit = 8,
): ChangelogEntry[] => {
  const sections = markdown.split(/\n## /);
  const entries: ChangelogEntry[] = [];

  for (let i = 0; i < sections.length && entries.length < limit; i++) {
    const meta = sections[i];
    const dateMatch = meta.match(DATE_RE);
    // A meta block is identified by a standalone date line.
    if (!dateMatch) continue;

    // The anchor lives in the body block that follows the meta block.
    const body = sections[i + 1] ?? "";
    const anchorMatch = body.match(ANCHOR_RE);
    if (!anchorMatch) continue;

    const title = meta.split("\n")[0].trim();

    entries.push({
      title,
      date: formatDate(dateMatch[1], dateMatch[2], dateMatch[3]),
      publishedAt: toTimestamptz(dateMatch[1], dateMatch[2], dateMatch[3]),
      tags: extractTags(meta),
      summary: extractSummary(body),
      anchor: anchorMatch[1],
      imageUrl: extractImage(body),
    });

    i++; // skip the body block we just consumed
  }

  return entries;
};
